import { AbstractStore } from 'stores/abstract';
import { FeatureFlagStore } from 'stores/FeatureFlagStore';
import * as Sentry from '@sentry/react';
import { isValidDataForCaching } from 'helpers/cacheValidation';

/**
 * Enhanced API Cache Store using browser's Cache API
 * More memory efficient than in-memory storage
 * Persists across page refreshes (until manually cleared)
 */
class CacheAPIStore extends AbstractStore {
  CACHE_NAME = 'pitstop-api-cache-v1';
  CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor() {
    super();
    this.initializeCache();
    this.reset();
  }

  /**
   * Initialize cache and clean up old versions
   * @returns {Promise<void>}
   */
  initializeCache = async () => {
    try {
      // Clean up old cache versions
      const cacheNames = await caches.keys();
      const oldCaches = cacheNames.filter(
        (name) =>
          name.startsWith('pitstop-api-cache-') && name !== this.CACHE_NAME
      );

      await Promise.all(oldCaches.map((name) => caches.delete(name)));

      await caches.open(this.CACHE_NAME);

      console.log('📦 Cache API initialized:', this.CACHE_NAME);
    } catch (error) {
      console.warn('Failed to initialize Cache API:', error);
    }
  };

  /**
   * Simple hash function for cache keys
   * @param {string} str
   * @returns {string}
   */
  simpleHash = (str) => {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  };

  /**
   * Generate cache key (matches existing interface)
   * @param {string} url
   * @param {string} method
   * @param {any} body
   * @returns {string}
   */
  generateKey = (url, method = 'GET', body = null) => {
    const bodyStr = body ? JSON.stringify(body) : '';
    const keyComponents = `${method}|${url}|${bodyStr}`;

    if (keyComponents.length <= 200) {
      return keyComponents;
    }

    return `${method}|${this.simpleHash(url)}|${this.simpleHash(bodyStr)}`;
  };

  /**
   * Generate Request object for Cache API
   * @param {string} url
   * @param {string} method
   * @param {any} body
   * @returns {Request}
   */
  generateRequest = (url, method = 'GET', body = null) => {
    const bodyStr = body ? JSON.stringify(body) : '';
    const timestamp = Date.now();

    const hashedKey = this.generateKey(url, method, body);
    const requestUrl = `cache-key-${hashedKey}`;

    const request = new Request(requestUrl, {
      method: 'GET',
      headers: {
        'x-original-method': method,
        'x-original-url': url,
        'x-cache-timestamp': timestamp.toString(),
        'x-cache-body': bodyStr,
      },
    });

    return request;
  };

  /**
   * Parse cache key into its components
   * @param {string} cacheKey - Cache key in format "method|url|body"
   * @returns {{method: string, url: string, body: any}}
   */
  parseCacheKey = (cacheKey) => {
    const parts = cacheKey.split('|');
    const method = parts[0] || 'GET';
    const url = parts[1] || '';
    const bodyStr = parts[2] || '';
    const body = bodyStr ? JSON.parse(bodyStr) : null;

    return { method, url, body };
  };

  /**
   * Check if cache entry is still valid
   * @param {Response} response
   * @returns {boolean}
   */
  isValidEntry = (response) => {
    try {
      const timestamp = parseInt(
        response.headers.get('x-cache-timestamp') || '0'
      );
      return Date.now() - timestamp < this.CACHE_DURATION;
    } catch {
      return false;
    }
  };

  /**
   * Get data from cache (matches existing interface)
   * @param {string} cacheKey - Cache key generated by generateKey
   * @returns {Promise<any>}
   */
  getData = async (cacheKey) => {
    const { method, url, body } = this.parseCacheKey(cacheKey);
    return this.getDataByParts(url, method, body);
  };

  /**
   * Get data from cache by URL parts
   * @param {string} url
   * @param {string} method
   * @param {any} body
   * @returns {Promise<any>}
   */
  getDataByParts = async (url, method = 'GET', body = null) => {
    if (!('caches' in window)) {
      this.logCacheMiss('Cache API not supported');
      return null;
    }

    try {
      const cache = await caches.open(this.CACHE_NAME);
      const request = this.generateRequest(url, method, body);
      const response = await cache.match(request);

      if (!response) {
        this.logCacheMiss(`${method}:${url}`);
        return null;
      }

      if (!this.isValidEntry(response)) {
        await cache.delete(request);
        this.logCacheMiss(`${method}:${url} (expired)`);
        return null;
      }

      const data = await response.json();
      this.logCacheHit(`${method}:${url}`);
      return data;
    } catch (error) {
      console.warn('Cache API read error:', error);
      return null;
    }
  };

  /**
   * Store data in cache (matches existing interface)
   * @param {string} cacheKey - Cache key generated by generateKey
   * @param {any} data - Data to store
   * @returns {Promise<void>}
   */
  setData = async (cacheKey, data) => {
    const { method, url, body } = this.parseCacheKey(cacheKey);
    return this.setDataByParts(url, method, body, data);
  };

  /**
   * Store data in cache by URL parts
   * @param {string} url
   * @param {string} method
   * @param {any} body
   * @param {any} data
   * @returns {Promise<void>}
   */
  setDataByParts = async (url, method = 'GET', body = null, data) => {
    // Prevent caching empty data as defense-in-depth validation
    if (!isValidDataForCaching(data)) {
      console.warn(
        '[Cache] Blocked caching of empty data for:',
        `${method}:${url}`
      );
      try {
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setTag('store', 'CacheAPIStore');
          scope.setExtra('endpoint', `${method}:${url}`);
          scope.setExtra('reason', 'empty-data');
          scope.setExtra('dataType', typeof data);
          Sentry.captureMessage('Blocked caching of empty data');
        });
      } catch (e) {
        // No-op: avoid throwing if Sentry isn't initialized
      }
      return;
    }

    if (!('caches' in window)) {
      return;
    }

    try {
      const cache = await caches.open(this.CACHE_NAME);
      const request = this.generateRequest(url, method, body);

      const response = new Response(JSON.stringify(data), {
        status: 200,
        statusText: 'OK',
        headers: {
          'Content-Type': 'application/json',
          'x-original-method': method,
          'x-original-url': url,
          'x-cache-timestamp': Date.now().toString(),
          'x-cache-body': body ? JSON.stringify(body) : '',
        },
      });

      await cache.put(request, response);
      this.logCacheSet(`${method}:${url}`);
    } catch (error) {
      console.warn('Cache API write error:', error);
    }
  };

  /**
   * Invalidate entries by pattern
   * @param {string} pattern
   * @returns {Promise<number>}
   */
  invalidatePattern = async (pattern) => {
    if (!('caches' in window)) {
      return 0;
    }

    try {
      const cache = await caches.open(this.CACHE_NAME);
      const requests = await cache.keys();

      const toDelete = requests.filter((request) => {
        const originalUrl =
          request.headers.get('x-original-url') || request.url;
        return originalUrl.includes(pattern);
      });

      await Promise.all(toDelete.map((request) => cache.delete(request)));

      if (toDelete.length > 0) {
        console.log(
          `🧹 Cache API: Invalidated ${toDelete.length} entries matching pattern: ${pattern}`
        );
      }

      return toDelete.length;
    } catch (error) {
      console.warn('Cache API invalidation error:', error);
      return 0;
    }
  };

  /**
   * Invalidate specific endpoint
   * @param {string} endpoint
   * @returns {Promise<number>}
   */
  invalidateEndpoint = async (endpoint) => {
    return this.invalidatePattern(endpoint);
  };

  /**
   * Get cache statistics
   * @returns {Promise<{total: number, valid: number, expired: number, size: number, sizeFormatted: string}>}
   */
  getCacheStats = async () => {
    if (!('caches' in window)) {
      return { total: 0, valid: 0, expired: 0, size: 0 };
    }

    try {
      const cache = await caches.open(this.CACHE_NAME);
      const requests = await cache.keys();

      let validEntries = 0;
      let expiredEntries = 0;
      let totalSize = 0;

      for (const request of requests) {
        const response = await cache.match(request);
        if (response) {
          const responseClone = response.clone();
          const text = await responseClone.text();
          totalSize += text.length;

          if (this.isValidEntry(response)) {
            validEntries++;
          } else {
            expiredEntries++;
          }
        }
      }

      return {
        total: requests.length,
        valid: validEntries,
        expired: expiredEntries,
        size: totalSize,
        sizeFormatted: this.formatBytes(totalSize),
      };
    } catch (error) {
      console.warn('Cache API stats error:', error);
      return { total: 0, valid: 0, expired: 0, size: 0 };
    }
  };

  /**
   * Clear all cache
   * @returns {Promise<void>}
   */
  reset = async () => {
    try {
      const stats = await this.getCacheStats();
      await caches.delete(this.CACHE_NAME);
      await caches.open(this.CACHE_NAME); // Recreate empty cache

      console.log(
        `🗑️ Cache API cleared. Had ${stats.total} entries (${stats.sizeFormatted})`
      );
    } catch (error) {
      console.warn('Cache API reset error:', error);
    }
  };

  /**
   * Clean up expired entries
   * @returns {Promise<void>}
   */
  cleanupExpired = async () => {
    if (!('caches' in window)) {
      return;
    }

    try {
      const cache = await caches.open(this.CACHE_NAME);
      const requests = await cache.keys();

      const expiredRequests = [];

      for (const request of requests) {
        const response = await cache.match(request);
        if (response && !this.isValidEntry(response)) {
          expiredRequests.push(request);
        }
      }

      await Promise.all(
        expiredRequests.map((request) => cache.delete(request))
      );

      if (expiredRequests.length > 0) {
        console.log(
          `🧹 Cache API: Cleaned up ${expiredRequests.length} expired entries`
        );
      }
    } catch (error) {
      console.warn('Cache API cleanup error:', error);
    }
  };

  /**
   * Format bytes to a human readable string
   * @param {*} bytes
   * @returns {string}
   */
  formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Log a cache hit event, only if debug mode is enabled
   * @param {*} key
   */
  logCacheHit = (key) => {
    if (FeatureFlagStore.isDebugModeEnabled) {
      console.log(`🟢 Cache API HIT: ${key}`);
    }
  };

  /**
   * Log a cache miss event, only if debug mode is enabled
   * @param {*} key
   */
  logCacheMiss = (key) => {
    if (FeatureFlagStore.isDebugModeEnabled) {
      console.log(`🔴 Cache API MISS: ${key}`);
    }
  };

  /**
   * Log a cache set event, only if debug mode is enabled
   * @param {*} key
   */
  logCacheSet = (key) => {
    if (FeatureFlagStore.isDebugModeEnabled) {
      console.log(`💾 Cache API SET: ${key}`);
    }
  };
}

export const singletonCacheAPIStore = new CacheAPIStore();

// Clean up expired entries every 5 minutes
setInterval(() => {
  singletonCacheAPIStore.cleanupExpired();
}, singletonCacheAPIStore.CACHE_DURATION);
